import Section from 'app/frontend/base/components/Section'
import {
  characterName,
  date,
  role,
  status,
  team,
} from 'app/frontend/base/config/column-definitions/application'
import {
  usePageInfo,
  useUserApplications,
} from 'app/frontend/base/config/queries'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataTable } from '@/components/ui/data-table'
import { CircleCheck, CircleQuestionMark } from 'lucide-react'
import { ColumnDef } from '@tanstack/react-table'
import Card from 'app/frontend/base/components/Card'

const UserApplications = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    data: { gameVersion },
  } = usePageInfo()

  const pendingColumns: ColumnDef<wowaudit.Application>[] = useMemo(
    () => [
      date,
      team,
      role({ withFilters: false }),
      characterName({ withFilters: false }),
      status({ withFilters: false }),
    ],
    [],
  )

  const processedColumns: ColumnDef<wowaudit.Application>[] = useMemo(
    () => [
      date,
      team,
      role({ withFilters: true }),
      characterName({ withFilters: true }),
      status({ withFilters: true }),
    ],
    [],
  )

  const { data: pendingApplications, isPending } = useUserApplications(true)
  const { data: processedApplications } = useUserApplications(false)

  const handleRowClick = (application: wowaudit.Application) => {
    navigate(`/profile/applications/${application.id}`, {
      state: {
        tableParams: Object.fromEntries(searchParams),
      },
    })
  }

  return (
    <Section title='Profile'>
      <h2 className='flex justify-between items-center'>
        <span className='flex items-center gap-3'>
          <span>My applications</span>
          <Card className='rounded-lg px-3 py-0 text-base'>
            {pendingApplications?.length}
          </Card>
        </span>
      </h2>

      <div className='mb-6'>
        <DataTable
          className='whitespace-nowrap'
          data={pendingApplications ?? []}
          columns={pendingColumns}
          includeHeader={true}
          emptyMessage={() => (
            <div
              className={`flex flex-col items-center gap-4 my-8 ${
                isPending ? 'opacity-0' : ''
              }`}
            >
              {processedApplications?.length ? (
                <>
                  <CircleCheck size={48} className='text-success-foreground' />{' '}
                  <span>You have no pending applications</span>
                </>
              ) : (
                <>
                  <CircleQuestionMark
                    size={48}
                    className='text-info-foreground'
                  />{' '}
                  <span>
                    You have never applied to any team on wowaudit (
                    {t(`game-versions.${gameVersion}.caption`)})
                  </span>
                </>
              )}
            </div>
          )}
          sortingState={[{ id: 'date', desc: true }]}
          getRowId={(application) => application.id.toString()}
          onRowClick={handleRowClick}
          rowClassName={() => `hover:bg-card-nested`}
        />
      </div>

      <div className='my-12'>
        {processedApplications?.length ? (
          <>
            <h2 className='text-xl font-semibold mb-3'>History</h2>
            <DataTable
              className='whitespace-nowrap'
              data={processedApplications ?? []}
              columns={processedColumns}
              includeHeader={true}
              sortingState={[{ id: 'date', desc: true }]}
              getRowId={(application) => application.id.toString()}
              pagination={20}
              filterType='includesString'
              onRowClick={handleRowClick}
              rowClassName={() => 'hover:bg-card-nested'}
            />
          </>
        ) : null}
      </div>
    </Section>
  )
}

export default UserApplications
